import { View } from "@common/types/views/views";

export const ACTIONS_COLUMN_ID = "actions";

/**
 * Keeps the row-actions column last and pinned right.
 *
 * The counterpart to `withExpandColumnFirst`, and applied on read for the same
 * reason: views cached before this column existed have no entry for it, so
 * appending it to the default view alone would leave every returning user
 * without a kebab. Normalizing on read fixes those views without a migration,
 * and `handleViewChange` then persists the corrected shape.
 *
 * Last in `view.columns` is what makes it rightmost — the right pin bucket is
 * ordered by `view.columns` (see buildColumns' `right` array) — so a user who
 * pins something else right still cannot get past it. `immutable` on the config
 * keeps the pin from being toggled off.
 */
export function withActionsColumnLast({ view }: { view: View }): View {
    const actionsIndex = view.columns.findIndex(
        (column) => column.id === ACTIONS_COLUMN_ID,
    );

    if (actionsIndex === -1) {
        return {
            ...view,
            columns: [
                ...view.columns,
                { id: ACTIONS_COLUMN_ID, options: { pin: "right" } },
            ],
        };
    }

    const actionsColumn = view.columns[actionsIndex];
    const isLast = actionsIndex === view.columns.length - 1;

    if (isLast && actionsColumn.options?.pin === "right") {
        return view;
    }

    return {
        ...view,
        columns: [
            ...view.columns.filter((_, index) => index !== actionsIndex),
            {
                ...actionsColumn,
                options: { ...actionsColumn.options, pin: "right" },
            },
        ],
    };
}
